/**
 * [INPUT]: 依赖 React 子节点分层/尺寸 memo、polar controller、CommonContext 与家族 Canvas
 * [OUTPUT]: 对外提供 PolarRoot，组装背景 SVG、dither Canvas、前景 SVG、DOM chrome 与 a11y 描述
 * [POS]: Pie/Donut/Radar 的结构根，拥有极坐标 pointer 命中与 selection
 * [PROTOCOL]: 变更时更新此头部，然后检查 CLAUDE.md
 */
"use client";
import { Children, isValidElement, useId, useMemo } from "react";
import { CommonChartContext } from "./common-context"
import { cn } from "./lib"
import { axisAtAngle, sliceAtAngle } from "./polar"
import { PolarChartContext, usePolarController } from "./polar-context"
import { useChartDimensions } from "./use-chart-dimensions"

const DEFAULT_POLAR_MARGINS = {
  top: 22,
  right: 14,
  bottom: 14,
  left: 14,
}

function layerOf(node) {
  if (!isValidElement(node) || typeof node.type === "string") return "svg"
  return (node.type).chartLayer ?? "svg";
}

export function PolarRoot(
  {
    chartType,
    Canvas,
    backDecoration,
    data,
    config,
    children,
    dataKey,
    nameKey,
    innerRadius = 0,
    margins: marginsProp,
    className,
    animate = true,
    animationDuration = 900,
    replayToken = 0,
    bloom = "off",
    bloomOnHover = false,
    defaultSelectedDataKey = null,
    onSelectionChange,
    ariaLabel = "数据图表",
    ariaDescription
  }
) {
  const { ref, size } = useChartDimensions()
  const descriptionId = useId()
  const marginTop = marginsProp?.top ?? DEFAULT_POLAR_MARGINS.top
  const marginRight = marginsProp?.right ?? DEFAULT_POLAR_MARGINS.right
  const marginBottom = marginsProp?.bottom ?? DEFAULT_POLAR_MARGINS.bottom
  const marginLeft = marginsProp?.left ?? DEFAULT_POLAR_MARGINS.left
  const margins = useMemo(() => ({
    top: marginTop,
    right: marginRight,
    bottom: marginBottom,
    left: marginLeft,
  }), [marginTop, marginRight, marginBottom, marginLeft])
  const CanvasLayer = Canvas

  const ctx = usePolarController({
    chartType,
    data,
    config,
    dataKey,
    nameKey,
    innerRadiusRatio: innerRadius,
    dimensions: size,
    margins,
    animate,
    animationDuration,
    replayToken,
    bloom,
    bloomOnHover,
    defaultSelectedDataKey,
    onSelectionChange,
  })

  const backChildren = []
  const svgChildren = []
  const domChildren = []
  Children.forEach(children, (child) => {
    const layer = layerOf(child)
    if (layer === "back") backChildren.push(child)
    else if (layer === "dom") domChildren.push(child)
    else svgChildren.push(child)
  })

  const onMove = (clientX, clientY) => {
    const el = ref.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const dx = clientX - rect.left - margins.left - ctx.center.x
    const dy = clientY - rect.top - margins.top - ctx.center.y
    const angle = Math.atan2(dy, dx)
    const r = Math.hypot(dx, dy)
    if (chartType === "pie" && ctx.pie) {
      const inside = r <= ctx.outerRadius && r >= ctx.innerRadius
      const i = inside ? sliceAtAngle(ctx.pie, angle) : -1
      ctx.setHoverIndex(i >= 0 ? i : null)
    } else if (ctx.radar) {
      ctx.setHoverIndex(axisAtAngle(ctx.radar.axes, angle))
    }
    ctx.setCursor(clientX - rect.left, clientY - rect.top)
  }

  return (
    <PolarChartContext value={ctx}>
      <CommonChartContext value={ctx.common}>
        <div
          ref={ref}
          data-slot="dither-chart"
          className={cn("relative h-full w-full", className)}
          onPointerEnter={() => ctx.setMouseInChart(true)}
          onPointerMove={(e) => onMove(e.clientX, e.clientY)}
          onPointerLeave={() => {
            ctx.setMouseInChart(false)
            ctx.setHoverIndex(null)
          }}>
          {ariaDescription && <span id={descriptionId} className="sr-only">{ariaDescription}</span>}
          {ctx.ready && (
            <svg
              width={size.width}
              height={size.height}
              className="absolute inset-0 overflow-visible"
              aria-hidden
              role="presentation">
              <g transform={`translate(${margins.left},${margins.top})`}>
                {backDecoration}
                {backChildren}
              </g>
            </svg>
          )}
          <CanvasLayer />
          {ctx.ready && (
            <svg
              width={size.width}
              height={size.height}
              className="absolute inset-0 overflow-visible"
              role="img"
              aria-label={ariaLabel}
              aria-describedby={ariaDescription ? descriptionId : undefined}>
              <g transform={`translate(${margins.left},${margins.top})`}>
                {svgChildren}
              </g>
            </svg>
          )}
          {domChildren}
        </div>
      </CommonChartContext>
    </PolarChartContext>
  );
}
